import { SelectionModel } from '@angular/cdk/collections';
import { Injectable } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CmLocalizationService } from '@ats/cm/core';
import { Unsubscribable } from '@ats/utility';
import { Papa } from 'ngx-papaparse';
import { Observable, pipe, Subject, Subscription } from 'rxjs';
import {
	distinctUntilChanged,
	filter,
	map,
	publishReplay,
	refCount,
	shareReplay,
	startWith,
	switchMap,
	takeUntil,
} from 'rxjs/operators';
import {
	CmITableConfig,
	CmTableColumn,
	CmTableConfig,
	CmTableRow,
	CmTableRowSummary,
} from '../models';

export const SELECT_COLUMN_NAME = 'cm-table-select';

const SEARCH_DELIMITER = '◬';

enum TableAction {
	CONFIG_UPDATE = 'Table config update',
	COLUMNS_UPDATE = 'Table columns update',
	ROWS_UPDATE = 'Table row update',
	SEARCH_UPDATE = 'Table search update',
	FILTER_UPDATE = 'Table filter update',
	SELECTION_INIT = 'Table selection added',
	COLUMNS_TO_SEARCH_UPDATE = 'Table search on columns',
}

const initWith = (action: TableAction) => {
	return pipe(startWith(action), publishReplay(1), refCount());
};

const filterTableActions = (actions: TableAction[]) => {
	return pipe(filter((act: TableAction) => actions.includes(act)));
};

class TableState {
	config: CmITableConfig = null;
	rows: CmTableRow[] = [];
	columns: CmTableColumn[] = [];
	trackByColumn: string;
	search: string = '';
	selectedFilter?: boolean = null;
	filters: { [key: string]: string[] } = {};
	columnToSearch: string | null = null;
	selection: SelectionModel<CmTableRow>;
	selectionChangeListener: Subscription;
}

@Injectable()
export class CmTableService extends Unsubscribable {
	/** Indicates when paginator and sort is set up. Adding data after paginator improves performance */
	private viewReady: { sort: boolean; paginator: boolean } = { sort: false, paginator: false };
	/** State data used by the service */
	private state = new TableState();
	/** Subject for sending table service events */
	private actionSubj$ = new Subject<TableAction>();
	/** Observable for subscribing to table service events */
	private action$ = this.actionSubj$.asObservable();

	/** Observable to notify when the table config has updated */
	private configChange$ = this.action$.pipe(
		filter(act => act !== TableAction.CONFIG_UPDATE),
		publishReplay(1),
		refCount()
	);

	/** Material datasource used for the table */
	private datasource: MatTableDataSource<CmTableRow> = new MatTableDataSource();

	/** Observable of the table column definitions */
	public columns$ = this.action$.pipe(
		initWith(TableAction.COLUMNS_UPDATE),
		filterTableActions([TableAction.COLUMNS_UPDATE]),
		map(() => this.state.columns),
		shareReplay({ refCount: true, bufferSize: 1 })
	);

	/** Observable of the table row data */
	public rows$ = this.action$.pipe(
		initWith(TableAction.ROWS_UPDATE),
		filterTableActions([TableAction.ROWS_UPDATE]),
		map(() => this.state.rows),
		shareReplay({ refCount: true, bufferSize: 1 })
	);

	/** Observable indicating when the config has been set */
	public configReady$: Observable<boolean> = this.action$.pipe(
		initWith(TableAction.CONFIG_UPDATE),
		filterTableActions([TableAction.CONFIG_UPDATE]),
		map(() => !!this.state.config),
		shareReplay({ refCount: true, bufferSize: 1 }),
		distinctUntilChanged()
	);

	/** Observable of the table summary row */
	public summary$ = this.configChange$.pipe(
		filter(() => !!this.state.columns.length && !!this.state.rows.length),
		map(() => this.parseColumnSummary(this.state.columns))
	);

	/** Observable indicating that the table has a summary row */
	public hasSummary$ = this.columns$.pipe(map(columns => !!columns.some(col => !!col.summary)));

	/** Observable of the current selections on a selectable table */
	public selection$ = this.action$.pipe(
		initWith(TableAction.SELECTION_INIT),
		filterTableActions([TableAction.SELECTION_INIT]),
		filter(() => typeof this.state.selection !== 'undefined'),
		switchMap(() => this.state.selection.changed.asObservable())
	);

	/** Observable string array of column IDs */
	public displayColumns$: Observable<string[]> = this.action$.pipe(
		initWith(TableAction.COLUMNS_UPDATE),
		filterTableActions([
			TableAction.COLUMNS_UPDATE,
			TableAction.CONFIG_UPDATE,
			TableAction.ROWS_UPDATE,
		]),
		filter(() => this.state.columns.length > 0),
		// If a column's hideIfEmpty is true, make sure not every value in that column is empty
		map(action =>
			action === TableAction.ROWS_UPDATE || TableAction.COLUMNS_UPDATE
				? this.state.columns.filter(
						col =>
							!col.hidden &&
							(!col.hideIfEmpty ||
								!(col.hideIfEmpty && this.state.rows.every(row => !row[col.field])))
					)
				: this.state.columns.filter(col => !col.hidden)
		),
		map(columns => columns.map(col => col.field)),
		map(columns =>
			(this.state.config || {}).selectable ? [SELECT_COLUMN_NAME, ...columns] : columns
		)
	);

	/** Observable of columns to search on */
	public searchedColumns$: Observable<
		{ field: string; label: string; selected: boolean; localizationKey?: string }[]
	> = this.action$.pipe(
		initWith(TableAction.COLUMNS_TO_SEARCH_UPDATE),
		filterTableActions([TableAction.COLUMNS_TO_SEARCH_UPDATE, TableAction.COLUMNS_UPDATE]),
		map(() =>
			this.state.columns
				.filter(col => col.searchable && col.type !== 'action' && col.type !== 'image')
				.map(col => ({
					field: col.field,
					label: col.label,
					selected: this.state.columnToSearch === col.field,
					localizationKey: col.localizationKey,
				}))
		),
		shareReplay({ refCount: true, bufferSize: 1 })
	);

	public trackBy: (row: CmTableRow) => any;

	constructor(private localizeService: CmLocalizationService) {
		super();
	}

	/** Getter for the material datasource */
	get tableDatasource() {
		return this.datasource;
	}

	/** Getter for the current table config */
	get config() {
		return this.state?.config;
	}

	/** Getter for the Material SelectionModel with the current selection state */
	get selection(): SelectionModel<CmTableRow> {
		return this.state.selection;
	}

	/** Getter for the selection filter  */
	get selectedFilter(): boolean | null {
		return this.state.selectedFilter;
	}

	/** Initializes the table
	 * @method
	 * @param {CmITableConfig} config Configuration for the table
	 */
	initialize(config: CmITableConfig) {
		this.state.config = new CmTableConfig(config);
		this.actionSubj$.next(TableAction.CONFIG_UPDATE);
	}

	/**
	 * Set up the sort and pagination
	 * @param {MatSort | MatPaginator} element The sort/pagination element to add to the datasource
	 */
	addViewChild(element: MatSort | MatPaginator | 'sort' | 'paginator') {
		if (typeof element === 'string') {
			this.viewReady[element] = true;
		} else {
			if (!this.datasource.sort && element instanceof MatSort) {
				this.datasource.sortingDataAccessor = this.caseInsensitiveSortingDataAccessor;
				this.datasource.sort = element;
				this.datasource.filterPredicate = this.filterPredicate;
				this.viewReady.sort = true;
			}
			if (!this.datasource.paginator && element instanceof MatPaginator) {
				this.datasource.paginator = element;
				this.viewReady.paginator = true;
			}
		}
		if (this.viewReady.paginator && this.viewReady.sort) {
			this.updateRows();

			if ((this.state.config || {}).selectable) {
				this.instantiateTableSelectionConfig();
			}
		}
	}

	/**
	 * Update the state with new columns
	 * @param {CmTableColumn[]} columns The column definitions for the table
	 * @return {Observable<CmTableColumn[]} Returns the observable of the state's columns
	 */
	updateColumns(columns: CmTableColumn[]) {
		if (!this.trackBy) {
			const trackedColumn = columns.find(col => col.tracked);
			if (trackedColumn) {
				this.state.trackByColumn = trackedColumn.field;
				this.trackBy = row => row[this.state.trackByColumn];
			}
		}
		this.state.columns = [...columns];
		this.actionSubj$.next(TableAction.COLUMNS_UPDATE);
		return this.columns$;
	}

	/**
	 * Sets new data for the datasource.
	 * @param {CmTableRow[]} rows Row data used for the table.
	 */
	setRows(rows: CmTableRow[]) {
		this.state.rows = [...rows];
		if ((this.state.config || {}).clearSelectionOnUpdate && this.state.selection) {
			this.state.selection.clear();
			if (this.state.config.isSelected) {
				this.datasource.data.forEach(row => {
					if (this.state.config.isSelected(row)) {
						this.state.selection.toggle(row);
					}
				});
			}
		}
		this.updateRows();
	}

	/**
	 * Updates the datasource data with the current state row data
	 */
	updateRows() {
		if (this.viewReady.sort && this.viewReady.paginator) {
			this.datasource.data = [...this.state.rows];
			this.actionSubj$.next(TableAction.ROWS_UPDATE);
		}
	}

	/**
	 * Update the current table search
	 * @param {string} searchVal Value to search on in the table data
	 */
	updateSearch(searchVal: string) {
		// Setting the datasource.filter value to an empty string clears ALL of the filters
		// because it does not run the filter predicate, so use the delimiter if it is empty
		const filteredSearch = searchVal.trim().toLowerCase() || SEARCH_DELIMITER;
		this.state.search = filteredSearch;
		this.datasource.filter = filteredSearch;
		this.actionSubj$.next(TableAction.SEARCH_UPDATE);
	}

	/**
	 * Toggle a column's searchability
	 * @param {string} [field] The id of the column to add/remove from search. Empty id will clear all.
	 */
	toggleColumnSearch = (field: string | null) => {
		this.state.columnToSearch = field;
		this.actionSubj$.next(TableAction.COLUMNS_TO_SEARCH_UPDATE);
		if (this.state.search !== '') {
			this.updateSearch(this.datasource.filter);
		}
	};

	/**
	 * Toggle a filter on the table
	 * @param {string} field The id of the column to apply the filter to
	 * @param {string[]} value The value to filter on
	 */
	toggleFilter = (field: string, value: string[]) => {
		const filters = { ...this.state.filters };
		filters[field] = [...value];
		this.state.filters = { ...filters };
		// Material's filter is not re-run on an empty string, so have to
		// use the delimiter used in the predicate defined below to dupe it
		this.datasource.filter = this.state.search || SEARCH_DELIMITER;
		this.actionSubj$.next(TableAction.FILTER_UPDATE);
	};

	/**
	 * Toggles the filter on selected rows
	 * @param {boolean|null} value Determines which selection state to filter on. Null shows all.
	 */
	updateSelectionFilter = (value: boolean | null) => {
		this.state.selectedFilter = value;
		this.datasource.filter = this.state.search || SEARCH_DELIMITER;
		this.actionSubj$.next(TableAction.FILTER_UPDATE);
	};

	/**
	 * Makes the table sort case insensitive
	 * @param {any} data Data object that is being accessed.
	 * @param {string} sortHeaderId The name of the column that represents the data.
	 * @return {string|number} Returns a string or a number.
	 */
	caseInsensitiveSortingDataAccessor(data: any, sortHeaderId: string): string | number {
		if (typeof data[sortHeaderId] === 'string') {
			return data[sortHeaderId].toLocaleLowerCase();
		}
		return data[sortHeaderId];
	}

	/**
	 * Parses filtered table data into a CSV file
	 * @returns {Blob} A blob of the CSV data ready for download
	 */
	exportAsCSV() {
		return this.fnParseRowsToCSV(this.datasource.filteredData, this.state.columns);
	}

	/**
	 * Used to track rows by unique value
	 * @param {number} idx Current row index
	 * @param {any} row Current row data
	 * @returns {any} Unique value to identify row
	 */
	columnTrackBy = (idx, row) => row.field;

	/**
	 * Determines of all rows are selected
	 * @returns {boolean} A flig indicating if all of the rows are selected
	 */
	isAllSelected = () =>
		this.state.selection?.selected?.length === this.datasource?.filteredData?.length;

	/**
	 * If all rows are selected, this unselects all of them. Otherwise, it selects all rows.
	 */
	masterToggle = (newState?: boolean) => {
		if (typeof newState !== 'undefined') {
			if (!newState) {
				this.state.selection?.clear();
			} else {
				this.state.selection?.select(...this.datasource.filteredData);
			}
		} else {
			this.isAllSelected()
				? this.state.selection?.clear()
				: this.state.selection?.select(...this.datasource.filteredData);
		}
	};

	/**
	 * Subscribes to the table row to run selection updates when data changes.
	 */
	private instantiateTableSelectionConfig() {
		this.rows$.pipe(takeUntil(this.unsub)).subscribe(() => {
			this.updateSelection();
		});
	}

	/**
	 * Initializes the state's selection model if not already intialized.
	 * Initializes subscription to the selection model's change event to re-run search
	 * and filters when selections update.
	 * Updates currently selected rows when the data changes.
	 * @param {CmTAbleRow[]} rows The table row data
	 */
	private updateSelection() {
		if (!this.state.selection) {
			const allowMultiSelect = !this.state.config.disableMultiselect;
			this.state.selection = new SelectionModel<CmTableRow>(
				allowMultiSelect,
				this.datasource.data.filter(row =>
					this.state.config.isSelected ? this.state.config.isSelected(row) : false
				)
			);
		}

		if (!this.state.selectionChangeListener) {
			this.state.selectionChangeListener = this.state.selection.changed
				.asObservable()
				.pipe(
					takeUntil(this.unsub),
					filter(() => this.state.selectedFilter !== null)
				)
				.subscribe(() => {
					this.datasource.filter = this.state.search || SEARCH_DELIMITER;
					this.actionSubj$.next(TableAction.FILTER_UPDATE);
				});
		}
		this.actionSubj$.next(TableAction.SELECTION_INIT);
	}

	/**
	 * Parses through the columns to build the summary row data
	 * @param {CmTableColumn[]} columns The table column definitions
	 * @returns {CmTableRowSummary[]} Data for the table's summary row
	 */
	private parseColumnSummary(columns: CmTableColumn[]): CmTableRowSummary {
		if (!columns.some(col => col.summary)) {
			return null;
		}
		const summary: CmTableRowSummary = {};
		columns.forEach(col => {
			summary[col.field] = !Object.hasOwnProperty.call(col, 'summary')
				? ''
				: col.summary(this.datasource.filteredData.map(row => row[col.summaryField]));
		});
		return summary;
	}

	/**
	 * Filter predicate used by Material to apply filters to the table data.
	 * @param {CmTableRow} row The current row for checking against the filter
	 * @returns {boolean} A flag indicating if the row is visible or not
	 */
	private filterPredicate = (row: CmTableRow) => {
		if (this.state.config?.selectable && this.state.selectedFilter !== null) {
			const rowKey = this.state.config.rowKey;
			const selections = this.state.selection?.selected.map(selection => selection[rowKey]);
			if (
				(this.state.selectedFilter === true && !selections.includes(row[rowKey])) ||
				(this.state.selectedFilter === false && selections.includes(row[rowKey]))
			) {
				return false;
			}
		}

		const columns = this.state.columns
			.filter(
				col =>
					col.searchable &&
					(!this.state.columnToSearch || this.state.columnToSearch === col.field)
			)
			.map(col => col.field);
		return this.fnFilterTableRows(row, this.state.filters, this.state.search, columns);
	};

	/**
	 * Pure function for applying filters to table data
	 * @param {CmTableRow} row Row data to filter on
	 * @param {[key: string]: string[]} filters Object containing current filter definitions.
	 * Each property should match a row property, and its value is an array of values to filter on.
	 * @param {string} search Search value to look for in searchable row properties
	 * @param {string[]} columns Column definitions for the table
	 * @param {string} delimiter Defaults to search delimiter constant. Delimiter used to separate row object properties when stringifying.
	 * @returns {boolean} Flag indicating if the row is visible
	 */
	private fnFilterTableRows = (
		row: CmTableRow,
		filters: { [key: string]: string[] },
		search: string,
		columns: string[],
		delimiter: string = SEARCH_DELIMITER
	) => {
		let filtered = false;
		Object.keys(filters).forEach(key => {
			filtered =
				filtered || (!!filters[key]?.length && !filters[key].includes(row[key].toString()));
		});

		if (filtered) {
			return false;
		}

		if (search === '') {
			return true;
		}

		const haystack = columns
			.reduce(
				(currentTerm: string, col: string) =>
					currentTerm + (row as { [col: string]: any })[col] + delimiter,
				''
			)
			.toLowerCase();
		const needle = search;

		return haystack.indexOf(needle) !== -1;
	};

	/**
	 * Pure function to parse table data to a CSV
	 * @param {CmTableRow[]} rows
	 * @param {CmTableColumn[]} columns
	 * @returns {Blob} A blob of a CSV ready for download
	 */
	private fnParseRowsToCSV = (rows: CmTableRow[], columns: CmTableColumn[]): Blob => {
		const exportableColumns = columns.filter(col => col.exportable);
		const headers = exportableColumns.map(col =>
			this.localizeService.localize(col.localizationKey, col.label)
		);
		const exportData = rows.map(row =>
			exportableColumns
				.map(col => col.exportField || col.field)
				.map(col => (row[col] || row[col] === 0 ? row[col] : ''))
		);
		const parser = new Papa();

		const parsedRows =
			'\uFEFF' + parser.unparse([...[headers], ...exportData], { header: true });
		return new Blob([parsedRows], { type: 'text/csv' });
	};
}
